import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  Stage,
  Layer,
  Image as KonvaImage,
  Line,
  Circle,
  Group,
} from "react-konva";

// Arrow silhouette: 12px-wide rectangular shaft + equilateral-triangle head
// (side 28), drawn as one closed polygon so the white outline traces the
// union — Konva's built-in Arrow strokes the head with the shaft width.
const ARROW_COLOR = "#FF0000";
const ARROW_OUTLINE_COLOR = "#FFFFFF";
const ARROW_OUTLINE_WIDTH = 1;
const ARROW_SHAFT_WIDTH = 12;
const ARROW_HEAD_SIDE = 28;
const ARROW_HEAD_HEIGHT = (ARROW_HEAD_SIDE * Math.sqrt(3)) / 2;
// Minimum drag distance (image pixels) before a pointer-up commits an arrow.
// Below this the gesture is treated as a click and discarded.
const MIN_ARROW_LENGTH = 6;
// Action area per spec: the start→end line offset 16px outward per side.
const ARROW_HIT_OFFSET = 16;
// Anchor handle radius in display pixels — independent of image scale.
const ANCHOR_RADIUS = 7;
const ANCHOR_STROKE_WIDTH = 2;

// Fetch the image as a blob (same-origin object URL) so the Stage export can
// read pixels without tainting the canvas. `no-store` is required: the photo
// preview loads the same presigned URL without CORS, and reusing that cached
// response here fails the CORS check. Cache-busting params would break the
// S3 signature.
const useImageElement = (src) => {
  const [image, setImage] = useState(null);
  useEffect(() => {
    if (!src) {
      setImage(null);
      return undefined;
    }
    let cancelled = false;
    let objectUrl = null;
    fetch(src, { mode: "cors", cache: "no-store" })
      .then((res) => {
        if (!res.ok) throw new Error(`Image fetch failed: ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        const img = new window.Image();
        img.onload = () => {
          if (!cancelled) setImage(img);
        };
        img.onerror = () => {
          if (!cancelled) setImage(null);
        };
        img.src = objectUrl;
      })
      .catch(() => {
        if (!cancelled) setImage(null);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);
  return image;
};

// Seven-point arrow outline in display coordinates, tip at (x2, y2). Arrows
// shorter than the head height collapse to head-only.
const arrowPolygonPoints = (x1, y1, x2, y2) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const px = -uy;
  const py = ux;
  const headBase = Math.max(len - ARROW_HEAD_HEIGHT, 0);
  const bx = x1 + ux * headBase;
  const by = y1 + uy * headBase;
  const ws = ARROW_SHAFT_WIDTH / 2;
  const wh = ARROW_HEAD_SIDE / 2;
  return [
    x1 + px * ws,
    y1 + py * ws,
    bx + px * ws,
    by + py * ws,
    bx + px * wh,
    by + py * wh,
    x2,
    y2,
    bx - px * wh,
    by - py * wh,
    bx - px * ws,
    by - py * ws,
    x1 - px * ws,
    y1 - py * ws,
  ];
};

const fitScale = (containerW, containerH, imageW, imageH) => {
  if (!imageW || !imageH || !containerW || !containerH) return 1;
  return Math.min(containerW / imageW, containerH / imageH);
};

const MarkupCanvas = forwardRef(
  (
    {
      imageUrl,
      arrows,
      inFlight,
      selectedId,
      onStart,
      onMove,
      onEnd,
      onSelect,
      onDeselect,
      onMoveArrow,
      onMoveAnchor,
      onDragSessionStart,
      onDragSessionEnd,
    },
    ref
  ) => {
    const image = useImageElement(imageUrl);
    const containerRef = useRef(null);
    const stageRef = useRef(null);
    // Anchor handles are edit-only chrome; hidden during export so the selected
    // arrow's handles never bake into the saved image (AS-923).
    const anchorGroupRef = useRef(null);
    const [container, setContainer] = useState({ w: 0, h: 0 });

    useEffect(() => {
      const el = containerRef.current;
      if (!el) return undefined;
      const measure = () => {
        const { width, height } = el.getBoundingClientRect();
        setContainer({ w: width, h: height });
      };
      measure();
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }, []);

    const naturalW = image?.naturalWidth || 0;
    const naturalH = image?.naturalHeight || 0;
    const scale = fitScale(container.w, container.h, naturalW, naturalH);
    const stageW = Math.max(0, Math.floor(naturalW * scale));
    const stageH = Math.max(0, Math.floor(naturalH * scale));
    const canRender = !!image && stageW > 0 && stageH > 0;

    useImperativeHandle(
      ref,
      () => ({
        getImageSize: () =>
          naturalW && naturalH ? { width: naturalW, height: naturalH } : null,
        toBlob: () =>
          new Promise((resolve, reject) => {
            const stage = stageRef.current;
            if (!stage) {
              reject(new Error("Canvas not ready"));
              return;
            }
            const anchors = anchorGroupRef.current;
            try {
              anchors?.hide();
              const canvas = stage.toCanvas({
                pixelRatio: scale > 0 ? 1 / scale : 1,
              });
              canvas.toBlob(
                (blob) => {
                  if (blob) resolve(blob);
                  else reject(new Error("Failed to render annotated image"));
                },
                "image/jpeg",
                0.92
              );
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error("Stage export failed:", err);
              reject(err);
            } finally {
              anchors?.show();
            }
          }),
      }),
      [scale, naturalW, naturalH]
    );

    const stagePointToImage = (stage) => {
      const pt = stage.getPointerPosition();
      if (!pt || !scale) return null;
      return { x: pt.x / scale, y: pt.y / scale };
    };

    // Only treat empty-canvas clicks as start-of-draw. Clicks on arrows or
    // anchor handles bubble up here too — Konva sets `e.target` to the actual
    // node hit, so we branch on it.
    const handleDown = (e) => {
      const stage = e.target.getStage();
      const hitStage = e.target === stage;
      if (!hitStage) return;
      onDeselect?.();
      const pt = stagePointToImage(stage);
      if (pt) onStart?.(pt);
    };
    const handleMove = (e) => {
      const pt = stagePointToImage(e.target.getStage());
      if (pt) onMove?.(pt);
    };
    const handleUp = (e) => {
      const pt = stagePointToImage(e.target.getStage());
      if (pt) onEnd?.(pt);
    };

    const renderArrow = (a, key, opts = {}) => {
      const isSelected = !!opts.selected;
      const interactive = !!opts.interactive;
      const x1 = a.x1 * scale;
      const y1 = a.y1 * scale;
      const x2 = a.x2 * scale;
      const y2 = a.y2 * scale;
      return (
        <Group
          key={key}
          draggable={interactive && isSelected}
          onClick={
            interactive
              ? (e) => {
                  e.cancelBubble = true;
                  onSelect?.(a.id);
                }
              : undefined
          }
          onTap={
            interactive
              ? (e) => {
                  e.cancelBubble = true;
                  onSelect?.(a.id);
                }
              : undefined
          }
          onDragStart={interactive ? () => onDragSessionStart?.() : undefined}
          onDragEnd={
            interactive
              ? (e) => {
                  const node = e.target;
                  const dx = node.x() / scale;
                  const dy = node.y() / scale;
                  if (dx !== 0 || dy !== 0) {
                    onMoveArrow?.(a.id, dx, dy);
                  }
                  node.position({ x: 0, y: 0 });
                  onDragSessionEnd?.();
                }
              : undefined
          }
        >
          <Line
            points={arrowPolygonPoints(x1, y1, x2, y2)}
            closed
            fill={ARROW_COLOR}
            stroke={ARROW_OUTLINE_COLOR}
            strokeWidth={ARROW_OUTLINE_WIDTH}
            hitStrokeWidth={ARROW_HIT_OFFSET * 2}
            lineJoin="miter"
          />
        </Group>
      );
    };

    const renderAnchor = (a, which) => {
      const cx = (which === "start" ? a.x1 : a.x2) * scale;
      const cy = (which === "start" ? a.y1 : a.y2) * scale;
      return (
        <Circle
          key={`${a.id}-anchor-${which}`}
          x={cx}
          y={cy}
          radius={ANCHOR_RADIUS}
          fill="#fff"
          stroke={ARROW_COLOR}
          strokeWidth={ANCHOR_STROKE_WIDTH}
          draggable
          onMouseDown={(e) => {
            e.cancelBubble = true;
          }}
          onTouchStart={(e) => {
            e.cancelBubble = true;
          }}
          onDragStart={() => onDragSessionStart?.()}
          onDragMove={(e) => {
            const x = e.target.x() / scale;
            const y = e.target.y() / scale;
            onMoveAnchor?.(a.id, which, x, y);
          }}
          onDragEnd={() => onDragSessionEnd?.()}
        />
      );
    };

    return (
      <div ref={containerRef} className="markup-canvas">
        {canRender ? (
          <Stage
            ref={stageRef}
            width={stageW}
            height={stageH}
            onPointerDown={handleDown}
            onPointerMove={handleMove}
            onPointerUp={handleUp}
          >
            <Layer listening={false}>
              <KonvaImage image={image} width={stageW} height={stageH} />
            </Layer>
            <Layer>
              {arrows.map((a) =>
                renderArrow(a, a.id, {
                  selected: a.id === selectedId,
                  interactive: true,
                })
              )}
              {inFlight && renderArrow(inFlight, "in-flight")}
              <Group ref={anchorGroupRef}>
                {arrows
                  .filter((a) => a.id === selectedId)
                  .flatMap((a) => [
                    renderAnchor(a, "start"),
                    renderAnchor(a, "end"),
                  ])}
              </Group>
            </Layer>
          </Stage>
        ) : (
          <div className="markup-canvas-loading">
            {imageUrl ? "Loading photo…" : ""}
          </div>
        )}
      </div>
    );
  }
);

MarkupCanvas.displayName = "MarkupCanvas";

export { MIN_ARROW_LENGTH };
export default MarkupCanvas;
